import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import {
  ApolloClient,
  gql,
  InMemoryCache,
  NormalizedCacheObject,
} from "@apollo/client";
import qs from "qs";
import { from, map, Observable } from "rxjs";

import { AcademyArticle, AnalyticsPage } from "./payload.types";

interface PayloadListResponse<T> {
  docs: T[];
}

export interface AcademyAppArticle {
  name: string;
  slug: string;
  subtitle: string;
}
export interface AcademyAppArticleEntry {
  paths: string[];
  articles: AcademyAppArticle[];
}

@Injectable({
  providedIn: "root",
})
export class PayloadAPIService {
  protected http = inject(HttpClient);

  endpoint = "https://payload.confect.io/api";

  g: ApolloClient<NormalizedCacheObject>;

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
    this.g = new ApolloClient({
      uri: "https://payload.confect.io/api/graphql",
      cache: new InMemoryCache(),
    });
  }

  getAnalyticsPages() {
    const query = qs.stringify(
      {
        depth: 1,
        limit: 1000,
        sort: "sort",
        where: {
          "category.slug": { equals: "public-in-confect-app" },
        },
      },
      { addQueryPrefix: true },
    );
    return this.http.get<PayloadListResponse<AnalyticsPage>>(
      `${this.endpoint}/analytics-pages${query}`,
    );
  }

  getCategories() {
    const query = qs.stringify(
      {
        depth: 1,
        limit: 1000,
        sort: "sort",
      },
      { addQueryPrefix: true },
    );
    return this.http.get<PayloadListResponse<any>>(
      `${this.endpoint}/analytics-tag-categories${query}`,
    );
  }

  getDesignInspirationArticles() {
    const query = qs.stringify(
      {
        depth: 1,
        limit: 1000,
        sort: "sort",
        where: {
          "subcategory.slug": { equals: "design-examples" },
        },
      },
      { addQueryPrefix: true },
    );
    return this.http.get<PayloadListResponse<AcademyArticle>>(
      `${this.endpoint}/academy-articles${query}`,
    );
  }

  getAppArticles(): Observable<AcademyAppArticleEntry[]> {
    const GET_APP_ARTICLES = gql(`
      query GetAppArticles {
        AcademyAppArticles {
          docs {
            paths
            articles {
              name
              slug
              subtitle
            }
          }
        }
      }      
    `);

    return from(this.g.query({ query: GET_APP_ARTICLES })).pipe(
      map((res) => res.data["AcademyAppArticles"]["docs"]),
    );
  }
}
